// src/context/CurrencyContext.js
// Handles automatic currency detection based on user's location
// and allows manual currency switching. Uses Shopify's @inContext
// directive to return prices in the correct local currency.

import React, { createContext, useContext, useState, useEffect } from 'react';

// Supported currencies — add more here as Shopify Markets are enabled
// Each entry maps to a Shopify CountryCode for the @inContext directive
export const CURRENCIES = [
    { code: 'EUR', symbol: '€', country: 'IE', label: 'EUR €' },
    { code: 'GBP', symbol: '£', country: 'GB', label: 'GBP £' },
];

// Map detected IP country codes to a supported currency
// EU countries default to EUR, others fall back to EUR if unsupported
const COUNTRY_TO_CURRENCY = {
    IE: 'EUR', DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR',
    NL: 'EUR', BE: 'EUR', AT: 'EUR', PT: 'EUR', FI: 'EUR',
    GR: 'EUR', LU: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR',
    LV: 'EUR', LT: 'EUR', CY: 'EUR', MT: 'EUR',
    GB: 'GBP',
};

const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
    // Default to EUR until detection completes
    const [selectedCurrency, setSelectedCurrency] = useState(
        CURRENCIES.find(c => c.code === 'EUR')
    );
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check for a previously saved user preference first
        const saved = localStorage.getItem('maisondoud_currency');
        if (saved) {
            const found = CURRENCIES.find(c => c.code === saved);
            if (found) {
                setSelectedCurrency(found);
                setLoading(false);
                return;
            }
        }

        // Auto-detect country via our own Netlify function (netlify/functions/geo.js)
        fetch('/.netlify/functions/geo')
            .then(res => res.json())
            .then(data => {
                const countryCode = data.country;
                const currencyCode = COUNTRY_TO_CURRENCY[countryCode] || 'EUR';
                const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];
                setSelectedCurrency(currency);
            })
            .catch(() => {
                // Detection failed — stay on EUR default
            })
            .finally(() => setLoading(false));
    }, []);

    // Change currency manually and save preference to localStorage
    const changeCurrency = (currencyCode) => {
        const found = CURRENCIES.find(c => c.code === currencyCode);
        if (found) {
            setSelectedCurrency(found);
            localStorage.setItem('maisondoud_currency', currencyCode);
        }
    };

    return (
        <CurrencyContext.Provider value={{
            currency: selectedCurrency,         // Current currency object { code, symbol, country, label }
            currencies: CURRENCIES,             // All supported currencies for the selector
            country: selectedCurrency.country,  // Shopify CountryCode for @inContext
            symbol: selectedCurrency.symbol,    // e.g. €, £, $
            changeCurrency,
            loading,
        }}>
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    const context = useContext(CurrencyContext);
    if (!context) throw new Error('useCurrency must be used within CurrencyProvider');
    return context;
}